import React, { useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import Divider from 'src/components/ui/Divider/Divider';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Input from 'src/components/ui/Input/Input';
import Checkbox from 'src/components/ui/Checkbox/Checkbox';
import Select, { Option } from 'src/components/ui/Select/Select';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import {
  CasesSettings,
  CasesSettingsIssue,
  useCaseSettingsQuery,
  useCaseSettingsRecipientsQuery,
  useCaseSettingsUpdate,
} from 'src/services/caseSettings';
import styles from './CaseSettings.module.scss';

type Channel = 'email' | 'sms';

type CaseSettingsForm = {
  enabled: boolean;
  use_ai: boolean;
  recipient_ids: number[];
  channels: Channel[];
  merchant_name: string;
  table_header_color: string;
};

const defaultForm: CaseSettingsForm = {
  enabled: true,
  use_ai: true,
  recipient_ids: [],
  channels: ['email'],
  merchant_name: '',
  table_header_color: '#2F3A8B',
};

const fromServer = (s: CasesSettings): CaseSettingsForm => ({
  enabled: s.enabled,
  use_ai: s.use_ai,
  recipient_ids: s.recipient_ids ?? [],
  channels: (s.channels ?? []) as Channel[],
  merchant_name: s.merchant_name ?? '',
  table_header_color: s.table_header_color ?? '#2F3A8B',
});

type ChannelCardProps = {
  icon: 'message' | 'chat';
  label: string;
  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
};

const ChannelCard: React.FC<ChannelCardProps> = ({ icon, label, selected, onToggle, disabled }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={classNames(styles.channelCard, {
        [styles.selected]: selected,
        [styles.cardDisabled]: disabled,
      })}>
      <div className={styles.checkboxSlot}>
        <Checkbox checked={selected} disabled={disabled} onChange={onToggle} />
      </div>
      <Icon name={icon} width={24} height={24} />
      <div className="flex flex-col">
        <span className="typo-text-12 text-faded">{t('settings.cases.channel')}</span>
        <span className="typo-text-14--bold text-full">{label}</span>
      </div>
    </button>
  );
};

type SettingsCardProps = {
  icon: IconName;
  title: string;
  description: React.ReactNode;
  active: boolean;
  onToggle: (value: boolean) => void;
  toggleDisabled?: boolean;
  children: React.ReactNode;
};

// A settings group with its own activation toggle in the header. The toggle
// gates only the rows rendered inside this card — each card is independent.
const SettingsCard: React.FC<SettingsCardProps> = ({
  icon,
  title,
  description,
  active,
  onToggle,
  toggleDisabled,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardIcon}>
          <Icon name={icon} width={20} height={20} />
        </div>
        <div className={styles.cardHeading}>
          <h3 className={classNames('typo-text-16--bold m-0', styles.cardTitle)}>{title}</h3>
          <div className="typo-text-12 text-faded flex flex-col gap-8">{description}</div>
        </div>
        <div className={styles.cardToggle}>
          <span className="typo-text-12 text-faded uppercase">
            {t(active ? 'settings.cases.active' : 'settings.cases.inactive')}
          </span>
          <Toggle checked={active} onToggle={onToggle} disabled={toggleDisabled} />
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
};

type SettingRowProps = {
  label: string;
  description: string;
  children: React.ReactNode;
};

const SettingRow: React.FC<SettingRowProps> = ({ label, description, children }) => (
  <div className={styles.row}>
    <div className={styles.label}>
      <span className="typo-text-14--bold text-full block">{label}</span>
      <span className="typo-text-12 text-faded mt-4 block">{description}</span>
    </div>
    <div className={styles.control}>{children}</div>
  </div>
);

interface CaseSettingsProps {
  hasWriteAccess?: boolean;
}

const CaseSettings: React.FC<CaseSettingsProps> = ({ hasWriteAccess = true }) => {
  const { t } = useTranslation();

  const { data: settings } = useCaseSettingsQuery();
  const { data: recipientsData } = useCaseSettingsRecipientsQuery();
  const updateMutation = useCaseSettingsUpdate();

  const [form, setForm] = useState<CaseSettingsForm>(defaultForm);

  useEffect(() => {
    if (settings) setForm(fromServer(settings));
  }, [settings]);

  const recipientOptions: Option[] = useMemo(
    () =>
      recipientsData?.map(r => ({
        value: String(r.id),
        label: r.name,
      })) ?? [],
    [recipientsData],
  );

  const recipientNamesById = useMemo(() => new Map((recipientsData ?? []).map(r => [r.id, r.name])), [recipientsData]);

  const formatIssue = (issue: CasesSettingsIssue): string => {
    const names = (issue.affected_recipient_ids ?? [])
      .map(id => recipientNamesById.get(id))
      .filter((n): n is string => Boolean(n));
    if (names.length === 0) return issue.message;
    return t(`errors.case_settings_issue_${issue.code}`, {
      names: names.join(', '),
      count: names.length,
      defaultValue: issue.message,
    });
  };

  const selectedRecipientOptions = useMemo(
    () => recipientOptions.filter(opt => form.recipient_ids.includes(Number(opt.value))),
    [recipientOptions, form.recipient_ids],
  );

  // Each card's rows are gated only by that card's own toggle.
  const aiFieldsDisabled = !hasWriteAccess || !form.use_ai;
  const notificationFieldsDisabled = !hasWriteAccess || !form.enabled;

  const hasIssues = (settings?.issues?.length ?? 0) > 0;
  const saveDisabled = !hasWriteAccess || hasIssues || updateMutation.isPending;

  const update = <K extends keyof CaseSettingsForm>(key: K, value: CaseSettingsForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleChannel = (channel: Channel) =>
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel],
    }));

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        body: {
          enabled: form.enabled,
          use_ai: form.use_ai,
          recipient_ids: form.recipient_ids,
          channels: form.channels,
          merchant_name: form.merchant_name,
          table_header_color: form.table_header_color,
        },
      });
      toast.success(t('success.case_settings_updated'));
    } catch (error) {
      logger.error('Failed to update case settings', error);
      toast.error(t('errors.case_settings_update_failed'));
    }
  };

  return (
    <div className={styles.root}>
      <SettingsCard
        icon="star"
        title={t('settings.cases.ai_card_title')}
        description={
          <>
            <p className="m-0">{t('settings.cases.ai_card_description_1')}</p>
            <p className="m-0">{t('settings.cases.ai_card_description_2')}</p>
          </>
        }
        active={form.use_ai}
        onToggle={v => update('use_ai', v)}
        toggleDisabled={!hasWriteAccess}>
        <SettingRow
          label={t('settings.cases.merchant_name')}
          description={t('settings.cases.merchant_name_description')}>
          <Input
            value={form.merchant_name}
            onChange={e => update('merchant_name', e.target.value)}
            placeholder=""
            disabled={aiFieldsDisabled}
          />
        </SettingRow>

        <Divider />

        <SettingRow
          label={t('settings.cases.email_table_header')}
          description={t('settings.cases.email_table_header_description')}>
          <div className={styles.colorRow}>
            <div className={styles.colorSwatchWrapper} style={{ backgroundColor: form.table_header_color }}>
              <input
                type="color"
                value={form.table_header_color}
                onChange={e => update('table_header_color', e.target.value.toUpperCase())}
                disabled={aiFieldsDisabled}
                aria-label={t('settings.cases.email_table_header')}
              />
            </div>
            <Input
              value={form.table_header_color}
              onChange={e => update('table_header_color', e.target.value.toUpperCase())}
              placeholder="#000000"
              disabled={aiFieldsDisabled}
              className="w-[160px]"
            />
          </div>
        </SettingRow>
      </SettingsCard>

      <SettingsCard
        icon="notifications"
        title={t('settings.cases.notifications_card_title')}
        description={<p className="m-0">{t('settings.cases.notifications_card_description')}</p>}
        active={form.enabled}
        onToggle={v => update('enabled', v)}
        toggleDisabled={!hasWriteAccess}>
        <SettingRow
          label={t('settings.cases.notification_recipients')}
          description={t('settings.cases.notification_recipients_description')}>
          <Select
            isMultiSelect
            isSearchable
            isClearable
            options={recipientOptions}
            defaultValue={selectedRecipientOptions}
            onChange={selected =>
              update(
                'recipient_ids',
                selected.map(o => Number(o.value)),
              )
            }
            placeholder={t('settings.cases.notification_recipients_placeholder')}
            disabled={notificationFieldsDisabled}
          />
        </SettingRow>

        <Divider />

        <SettingRow label={t('settings.cases.channels')} description={t('settings.cases.channels_description')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            <ChannelCard
              icon="message"
              label={t('settings.cases.channel_email')}
              selected={form.channels.includes('email')}
              onToggle={() => !notificationFieldsDisabled && toggleChannel('email')}
              disabled={notificationFieldsDisabled}
            />
            <ChannelCard
              icon="chat"
              label={t('settings.cases.channel_sms')}
              selected={form.channels.includes('sms')}
              onToggle={() => !notificationFieldsDisabled && toggleChannel('sms')}
              disabled={notificationFieldsDisabled}
            />
          </div>
        </SettingRow>
      </SettingsCard>

      {hasIssues && (
        <div className={styles.issuesBox}>
          <Icon name="warning" width={18} height={18} />
          <ul className="typo-text-14 text-full m-0 pl-16 list-disc">
            {settings?.issues?.map(issue => <li key={`${issue.code}-${issue.channel}`}>{formatIssue(issue)}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-32 flex items-center justify-end gap-x-12">
        <Button
          title={t('general.save_changes')}
          tone={ButtonToneProps.PRIMARY_BLUE}
          onClick={handleSave}
          disabled={saveDisabled}
        />
      </div>
    </div>
  );
};

export default CaseSettings;
